import type { CSSProperties, FunctionComponent, ReactNode } from 'react';
import { useMemo } from 'react';
import { useAuth } from 'react-oidc-context';

import { Footer } from '../../footer/containers/footer';
import { LargeTopNavigation } from '../../main/containers/large-top-navigation/large-top-navigation';
import { Meta } from '../../shared/components/meta/meta';
import { Toaster } from '../../shared/components/toaster/toaster';
import { useDesktop } from '../../shared/hooks/breakpoint';
import { createStyleHelper } from '../../shared/utils/class-names';
import {
    convertProductToStructuredData,
    getMetatagsFromMetadata,
} from '../../shared/utils/metadata';
import { CompactNavigationBar } from '../components/compact-navigation-bar/compact-navigation-bar';
import { GameSeoContent } from '../components/game-seo-content/game-seo-content';
import { SimilarGames } from '../components/similar-games/similar-games';
import { useGameData } from '../hooks/use-game-data';

import styles from './gameplay.module.scss';

const style = createStyleHelper(styles, 'gameplay-layout');

export interface IGameplayLayoutProps {
    frameArea?: ReactNode;
    slugs: string[];
    onClose: () => void;
}

export const GameplayLayout: FunctionComponent<IGameplayLayoutProps> = ({
    frameArea,
    slugs,
    onClose,
}) => {
    const isDesktop = useDesktop();
    const auth = useAuth();
    const { slots } = useGameData({
        initialSlugs: slugs,
    });

    const firstAvailableGame = useMemo(() => slots.find(({ game }) => !!game)?.game, [slots]);

    const firstGameWithBackground = useMemo(
        () => slots.find(({ game }) => !!game?.gameplayBackground),
        [slots],
    );
    const background = firstGameWithBackground?.game?.gameplayBackground;

    const gameSlugs = useMemo(
        () =>
            slots
                .filter(({ game }) => {
                    return !!game;
                })
                .map((item) => {
                    return item.game?.slug ?? '';
                }),
        [slots],
    );

    return (
        <>
            {firstAvailableGame ? (
                <Meta
                    tags={[
                        ...getMetatagsFromMetadata(firstAvailableGame.metatags, {
                            pageTitle: firstAvailableGame.name,
                            description: firstAvailableGame.description,
                        }),
                        convertProductToStructuredData(
                            firstAvailableGame.structuredData,
                            firstAvailableGame,
                        ),
                    ]}
                />
            ) : null}
            <div
                className={style()}
                style={
                    {
                        '--gameplay-background': background ? `url(${background})` : '',
                    } as CSSProperties
                }
            >
                <div className={style('container')}>
                    {!isDesktop ? (
                        <CompactNavigationBar onClose={onClose} />
                    ) : (
                        <LargeTopNavigation className={style('navigation')} />
                    )}
                    <div className={style('frame-area')}>
                        <Toaster className={style('toaster')} />
                        {frameArea}
                    </div>
                </div>

                {!auth.isAuthenticated && !auth?.user && firstAvailableGame ? (
                    <div className={style('seo-content')}>
                        <GameSeoContent
                            gameInfoDescription={firstAvailableGame.description}
                            gameRating={firstAvailableGame.rating}
                            gameRatingDescription={firstAvailableGame.ratingDescription}
                            gameInfoDescriptionTitle={firstAvailableGame.descriptionTitle}
                        />
                    </div>
                ) : null}

                {isDesktop && auth.isAuthenticated && auth?.user && gameSlugs.length ? (
                    <SimilarGames gameSlugs={gameSlugs} className={style('similar-games')} />
                ) : null}

                <Footer />
            </div>
        </>
    );
};
